import React, { useState } from 'react';
import { imageFromUrl, brandFromWeb, BrandResult, contrastText } from '../lib/extras';

// Doplňkové zdroje obrázku pod galerií ikon: obrázek z odkazu (Disk Google, Dropbox, web)
// a „Logo z webu firmy“ (logo + barvy značky). Výsledek jde stejnou cestou jako nahraný soubor.
interface Props {
  allowBrand: boolean;
  onImage: (dataUrl: string, name: string) => void;
  onBrandColors?: (background: string, text: string) => void;
}

const btn = 'flex-1 py-2.5 px-3 bg-white text-gray-800 text-[10px] font-black uppercase tracking-widest rounded-md border-2 border-gray-200 hover:border-gray-400 transition-all flex items-center justify-center gap-2';
const input = 'flex-1 min-w-0 px-3 py-2.5 border-2 border-gray-300 rounded-md focus:border-red-600 outline-none text-sm font-bold bg-gray-50';

const ImageImportTools: React.FC<Props> = ({ allowBrand, onImage, onBrandColors }) => {
  const [mode, setMode] = useState<null | 'url' | 'brand'>(null);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brand, setBrand] = useState<BrandResult | null>(null);

  const open = (m: 'url' | 'brand') => { setMode(mode === m ? null : m); setError(null); setBrand(null); setUrl(''); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true); setError(null); setBrand(null);
    try {
      if (mode === 'url') {
        const r = await imageFromUrl(url.trim());
        onImage(r.dataUrl, r.name);
        setMode(null); setUrl('');
      } else {
        const r = await brandFromWeb(url.trim());
        if (!r.logos.length && !r.colors.length) setError('Na webu jsme logo ani barvy nenašli. Logo můžete nahrát jako soubor.');
        setBrand(r);
      }
    } catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <button type="button" className={btn} onClick={() => open('url')}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
          Z odkazu
        </button>
        {allowBrand && (
          <button type="button" className={btn} onClick={() => open('brand')}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" /></svg>
            Logo z webu firmy
          </button>
        )}
      </div>
      <p className="text-[10px] text-gray-400 text-center">Obrázek můžete také vložit přes Ctrl+V nebo ho přetáhnout do galerie. Umíme JPG, PNG, SVG, HEIC z iPhonu, PDF, AI, EPS, PSD i TIFF.</p>

      {mode && (
        <form onSubmit={submit} className="p-3 bg-gray-50 border-2 border-gray-200 rounded-md space-y-3">
          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">
            {mode === 'url' ? 'Odkaz na obrázek (i sdílený z Disku Google nebo Dropboxu)' : 'Adresa webu firmy'}
          </label>
          <div className="flex gap-2">
            <input autoFocus value={url} onChange={e => setUrl(e.target.value)} className={input} placeholder={mode === 'url' ? 'https://…' : 'např. www.firma.cz'} inputMode="url" />
            <button type="submit" disabled={busy || !url.trim()} className="px-4 py-2.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest rounded-md disabled:opacity-50">{busy ? 'Načítám…' : 'Načíst'}</button>
          </div>
          {error && <div className="p-2 text-xs font-bold bg-red-50 text-red-700 border-2 border-red-100 rounded-md">{error}</div>}

          {brand && (
            <div className="space-y-3">
              {brand.logos.length > 0 && (
                <>
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Vyberte logo{brand.title ? ` – ${brand.title}` : ''}</p>
                  <div className="grid grid-cols-4 gap-2">
                    {brand.logos.map((l, i) => (
                      <button type="button" key={i} onClick={() => { onImage(l.dataUrl, brand.site || 'logo'); setMode(null); }}
                        className="aspect-square rounded-md border-2 border-gray-200 hover:border-red-600 bg-white p-1.5 flex items-center justify-center"
                        style={{ backgroundImage: 'linear-gradient(45deg,#eee 25%,transparent 25%,transparent 75%,#eee 75%),linear-gradient(45deg,#eee 25%,transparent 25%,transparent 75%,#eee 75%)', backgroundSize: '10px 10px', backgroundPosition: '0 0,5px 5px' }}>
                        <img src={l.dataUrl} alt={`Logo ${i + 1}`} className="max-w-full max-h-full object-contain" />
                      </button>
                    ))}
                  </div>
                </>
              )}
              {brand.colors.length > 0 && onBrandColors && (
                <>
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Barvy značky – kliknutím použijete jako pozadí</p>
                  <div className="flex flex-wrap gap-2">
                    {brand.colors.map(c => (
                      <button type="button" key={c} title={c} onClick={() => onBrandColors(c, contrastText(c))}
                        className="w-10 h-10 rounded-md border-2 border-gray-300 hover:scale-105 transition-transform shadow-sm text-[9px] font-black"
                        style={{ background: c, color: contrastText(c) }}>Aa</button>
                    ))}
                  </div>
                </>
              )}
              <p className="text-[10px] text-gray-400">Logo použijte jen pokud k němu máte práva (vlastní firma, škola, klub).</p>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default ImageImportTools;
